/**
 * Sidebar Footer Component
 *
 * Always-visible footer with Resources section and social icons.
 * Matches the mobile "More" sheet structure for consistency.
 */

import { Instagram, Youtube, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EXTERNAL_LINKS, RESOURCE_LINKS } from '@/lib/constants/navigation';
import { RpcStatusNav } from '@/components/nav/RpcStatusNav';

// Twitter/X icon component
function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// TikTok icon component
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.73a8.19 8.19 0 0 0 4.76 1.52V6.79a4.84 4.84 0 0 1-1-.1z" />
    </svg>
  );
}

export function SidebarFooter() {
  return (
    <div className="mt-auto border-t border-border px-4 pt-4 pb-4">
      {/* Resources Section Header */}
      <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
        Resources
      </h3>

      {/* Resource Links (vertical list) */}
      <div className="flex flex-col gap-1.5 mb-4">
        {RESOURCE_LINKS.map((link) =>
          link.isExternal ? (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-foreground hover:text-primary transition-colors"
            >
              {link.label}
            </a>
          ) : (
            <Link
              key={link.href}
              to={link.href}
              className="text-sm text-foreground hover:text-primary transition-colors"
            >
              {link.label}
            </Link>
          )
        )}
      </div>

      {/* Social Icons Row - order matches mobile: X, IG, YT, TT, Email */}
      <div className="flex items-center gap-3 mb-3">
        <a
          href={EXTERNAL_LINKS.twitter}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Twitter"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <TwitterIcon className="w-4 h-4" />
        </a>
        <a
          href={EXTERNAL_LINKS.instagram}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Instagram className="w-4 h-4" />
        </a>
        <a
          href={EXTERNAL_LINKS.youtube}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="YouTube"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Youtube className="w-4 h-4" />
        </a>
        <a
          href={EXTERNAL_LINKS.tiktok}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="TikTok"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <TikTokIcon className="w-4 h-4" />
        </a>
        <a
          href={EXTERNAL_LINKS.email}
          aria-label="Email"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Mail className="w-4 h-4" />
        </a>
      </div>

      {/* RPC Status Row */}
      <div className="flex items-center">
        <RpcStatusNav />
      </div>
    </div>
  );
}
